import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Avatar,
  Stack,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider
} from '@mui/material';
import {
  School,
  TrendingUp,
  EmojiEvents,
  AccessTime,
  PlayArrow,
  CheckCircle,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';

const DashboardPage: React.FC = () => {
  const { t } = useTranslation();
  
  // 设置页面标题
  usePageTitle('dashboard');
  
  const { user } = useAuth();

  const learningStats = {
    completedCourses: 2,
    inProgressCourses: 1,
    totalHours: 85,
    certificatesEarned: 2,
  };

  const enrolledCourses = [
    {
      id: 1,
      title: 'Web3.0基础与区块链技术',
      instructor: 'Dr. Bob Chen',
      progress: 100,
      status: 'completed',
      duration: '40小时',
      completedAt: '2024-01-15',
    },
    {
      id: 2,
      title: '人工智能与机器学习导论',
      instructor: 'Dr. Alice Wang',
      progress: 100,
      status: 'completed',
      duration: '60小时',
      completedAt: '2024-02-20',
    },
    {
      id: 3,
      title: '智能合约开发实战',
      instructor: 'Prof. David Li',
      progress: 65,
      status: 'in-progress',
      duration: '50小时',
      nextLesson: '第8章: DeFi协议开发',
    },
  ];

  const recentAchievements = [
    {
      id: 1,
      title: 'Web3.0基础认证',
      description: '完成Web3.0基础课程',
      date: '2024-01-15',
      type: 'certificate',
    },
    {
      id: 2,
      title: 'AI基础认证',
      description: '完成AI与机器学习导论',
      date: '2024-02-20',
      type: 'certificate',
    },
    {
      id: 3,
      title: '学习达人',
      description: '连续学习30天',
      date: '2024-03-01',
      type: 'badge',
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in-progress':
        return 'primary';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return '已完成';
      case 'in-progress':
        return '学习中';
      default:
        return '未开始';
    }
  };

  return (
    <Box>
      {/* Welcome Header */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" spacing={3}>
            <Avatar sx={{ width: 80, height: 80, bgcolor: 'white', color: 'primary.main' }}>
              {user?.fullName?.charAt(0) || 'U'}
            </Avatar>
            <Box>
              <Typography variant="h3" component="h1" gutterBottom>
                欢迎回来，{user?.fullName || user?.username}！
              </Typography>
              <Typography variant="h6">
                继续您的学习之旅，探索AI和Web3.0的无限可能
              </Typography>
            </Box>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Learning Statistics */}
        <Grid container spacing={4} sx={{ mb: 6 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                <Typography variant="h4" color="success.main">
                  {learningStats.completedCourses}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  已完成课程
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <School sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h4" color="primary.main">
                  {learningStats.inProgressCourses}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  学习中课程
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <AccessTime sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
                <Typography variant="h4" color="warning.main">
                  {learningStats.totalHours}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  学习小时数
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <EmojiEvents sx={{ fontSize: 48, color: 'secondary.main', mb: 2 }} />
                <Typography variant="h4" color="secondary.main">
                  {learningStats.certificatesEarned}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  获得证书
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={4}>
          {/* My Courses */}
          <Grid item xs={12} lg={8}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom>
                我的课程
              </Typography>
              
              <Stack spacing={3}>
                {enrolledCourses.map((course) => (
                  <Card key={course.id} variant="outlined">
                    <CardContent>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={8}>
                          <Typography variant="h6" gutterBottom>
                            {course.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            讲师: {course.instructor} • 时长: {course.duration}
                          </Typography>
                          
                          <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="body2">
                                进度: {course.progress}%
                              </Typography>
                              <Chip
                                label={getStatusText(course.status)}
                                color={getStatusColor(course.status) as any}
                                size="small"
                              />
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={course.progress}
                              sx={{ height: 8, borderRadius: 4 }}
                            />
                          </Box>
                          
                          {course.status === 'completed' ? (
                            <Typography variant="body2" color="success.main">
                              ✅ 完成于 {course.completedAt}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="primary.main">
                              📚 下一节课: {course.nextLesson}
                            </Typography>
                          )}
                        </Grid>
                        
                        <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                          <Stack spacing={1}>
                            <Button
                              variant={course.status === 'completed' ? 'outlined' : 'contained'}
                              component={Link}
                              to={`/courses/${course.id}`}
                              startIcon={course.status === 'completed' ? <CheckCircle /> : <PlayArrow />}
                            >
                              {course.status === 'completed' ? '查看证书' : '继续学习'}
                            </Button>
                            {course.status === 'completed' && (
                              <Button
                                variant="text"
                                size="small"
                                component={Link}
                                to={`/courses/${course.id}/review`}
                              >
                                复习内容
                              </Button>
                            )}
                          </Stack>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
              
              <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Button variant="outlined" component={Link} to="/courses" size="large">
                  浏览更多课程
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Recent Achievements */}
          <Grid item xs={12} lg={4}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom>
                最近成就
              </Typography>
              
              <Stack spacing={2}>
                {recentAchievements.map((achievement) => (
                  <Card key={achievement.id} variant="outlined">
                    <CardContent sx={{ p: 2 }}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar
                          sx={{
                            bgcolor: achievement.type === 'certificate' ? 'success.main' : 'warning.main',
                          }}
                        >
                          <EmojiEvents />
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="subtitle2">
                            {achievement.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {achievement.description}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {achievement.date}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
              
              <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Button variant="text" component={Link} to="/profile">
                  查看所有成就
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Quick Actions */}
        <Paper sx={{ p: 3, mt: 4 }}>
          <Typography variant="h5" gutterBottom>
            快捷操作
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                variant="outlined"
                fullWidth
                size="large"
                component={Link}
                to="/courses"
                startIcon={<School />}
              >
                浏览课程
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                variant="outlined"
                fullWidth
                size="large"
                component={Link}
                to="/web3"
                startIcon={<EmojiEvents />}
              >
                Web3中心
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                variant="outlined"
                fullWidth
                size="large"
                component={Link}
                to="/profile"
                startIcon={<TrendingUp />}
              >
                学习统计
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                variant="outlined"
                fullWidth
                size="large"
                component={Link}
                to="/help"
                startIcon={<AccessTime />}
              >
                学习计划
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
};

export default DashboardPage; 